import { t } from "i18next";
import { ComponentProps, Fragment, ReactNode } from "react";
import { ComputeFieldComponent } from "../computeFields";
import { ArtworkInExhibition, Exhibition } from "../types";
import { SeparatedList } from "./shared/utils";

type ExhibitionVenueInput = Pick<
  Exhibition,
  | "expositionsDescriptionDatesChoixInconnues"
  | "expositionsDescriptionDatesChoixIncertaines"
  | "expositionsDescriptionDates"
  | "expositionsDescriptionLieuIncertain"
  | "expositionsDescriptionLieu"
  | "expositionsDescriptionCityIncertain"
  | "expositionsDescriptionVille"
  | "expositionsDescriptionCountryIncertain"
  | "expositionsDescriptionPays"
>;

type ExhibitionTitleInput = Pick<
  Exhibition,
  | "expositionsDescriptionTitreIncertain"
  | "expositionsDescriptionTitre"
  | "expositionsDescriptionTitreDOrigine"
>;

/** Dates + lieu + ville + pays (sans titre). */
export const ExhibitionVenueCF: ComputeFieldComponent<ExhibitionVenueInput> = ({
  input,
  language,
  ...props
}) => {
  return (
    <SeparatedList
      {...props}
      lineBreak={false}
      language={language}
      commaSeparated
      input={[
        input.expositionsDescriptionDatesChoixInconnues
          ? t("computeField.sansDateAbbrege", { lng: language })
          : input.expositionsDescriptionDatesChoixIncertaines
            ? `[${input.expositionsDescriptionDates}]`
            : input.expositionsDescriptionDates,
        input.expositionsDescriptionLieuIncertain
          ? `[${input.expositionsDescriptionLieu}]`
          : input.expositionsDescriptionLieu,
        input.expositionsDescriptionCityIncertain
          ? `[${input.expositionsDescriptionVille}]`
          : input.expositionsDescriptionVille,
        input.expositionsDescriptionCountryIncertain
          ? `[${input.expositionsDescriptionPays}]`
          : input.expositionsDescriptionPays,
      ]}
    />
  );
};

export const ExhibitionTitleCF: ComputeFieldComponent<ExhibitionTitleInput> = ({
  input,
}) => {
  return (
    <>
      {input.expositionsDescriptionTitreIncertain ? (
        `[${input.expositionsDescriptionTitre}]`
      ) : (
        <i>{input.expositionsDescriptionTitre}</i>
      )}
      {input.expositionsDescriptionTitreDOrigine
        ? ` (${input.expositionsDescriptionTitreDOrigine})`
        : null}
    </>
  );
};

function formatItinerantNote(note: string | undefined | null): ReactNode {
  const trimmed = (note ?? "").trim();
  if (!trimmed) return null;
  const first = trimmed[0];
  if (first === "(" || first === "[") {
    return <> {trimmed}</>;
  }
  return <>, {trimmed}</>;
}

function parseYear(exhibition: Exhibition): number {
  const year = parseInt(exhibition.expositionsDescriptionAnnee || "", 10);
  if (!Number.isNaN(year)) return year;
  const dates = exhibition.expositionsDescriptionDates || "";
  const match = dates.match(/\b(19|20)\d{2}\b/);
  if (match) return parseInt(match[0], 10);
  return Number.MAX_SAFE_INTEGER;
}

export function pickOldestExhibition(
  exhibitions: Exhibition[],
): Exhibition | undefined {
  if (!exhibitions.length) return undefined;
  return [...exhibitions].sort((a, b) => {
    const ya = parseYear(a);
    const yb = parseYear(b);
    if (ya !== yb) return ya - yb;
    return (a.expositionsDescriptionNumeroExpo || "").localeCompare(
      b.expositionsDescriptionNumeroExpo || "",
      undefined,
      { numeric: true },
    );
  })[0];
}

type ItinerantStopInput = {
  exhibition: Exhibition;
  catalogNumber?: string;
  note?: string;
};

/**
 * Étapes dans l'ordre fourni (ordre liste / itinerantOrder).
 * Titre = 1re étape de la liste (par défaut la plus ancienne si tri date).
 * Separator = DB translation `computeField.semicolon` (missing key → key itself).
 */
export const ItinerantExhibitionsGroupCF: ComputeFieldComponent<{
  stops: ItinerantStopInput[];
}> = ({ input, language, ...props }) => {
  const stops = input.stops ?? [];
  if (stops.length === 0) return <></>;

  const titleExhibition = stops[0]?.exhibition;
  if (!titleExhibition) return <></>;
  const semicolon = t("computeField.semicolon", { lng: language });

  return (
    <>
      {stops.map((stop, index) => {
        const venue = (
          <ExhibitionVenueCF
            input={stop.exhibition}
            language={language}
            {...props}
          />
        );
        const cat =
          stop.catalogNumber && stop.catalogNumber.trim() !== "" ? (
            <>
              {t("computeField.numeroCatalogueAbbrege", { lng: language })}
              {stop.catalogNumber}
            </>
          ) : null;

        const venueAndCat = (
          <SeparatedList
            {...props}
            lineBreak={false}
            language={language}
            commaSeparated
            input={[venue, cat]}
          />
        );

        if (index === 0) {
          return (
            <Fragment key={stop.exhibition.id + "-0"}>
              <SeparatedList
                {...props}
                lineBreak={false}
                language={language}
                commaSeparated
                input={[
                  <ExhibitionTitleCF
                    key="title"
                    input={titleExhibition}
                    language={language}
                    {...props}
                  />,
                  venueAndCat,
                ]}
              />
              {formatItinerantNote(stop.note)}
            </Fragment>
          );
        }

        return (
          <Fragment key={stop.exhibition.id + "-" + index}>
            {semicolon}
            {venueAndCat}
            {formatItinerantNote(stop.note)}
          </Fragment>
        );
      })}
    </>
  );
};

type RelatedExhibitionLink = Pick<
  ArtworkInExhibition,
  | "exhibition"
  | "oeuvresDansExpositionsDescriptionNumeroCatalogue"
  | "itinerantGroupId"
  | "itinerantOrder"
  | "itinerantNote"
> & {
  id?: string;
};

/**
 * Groups itinerant links into one CF entry; classic links stay line-by-line.
 */
export function partitionAndRenderRelatedExhibitions<
  T extends {
    exhibition: Exhibition;
    itinerantGroupId?: string | null;
    itinerantOrder?: number | null;
    itinerantNote?: string;
    id?: string;
  },
>({
  links,
  ClassicLine,
  language,
  lineBreak = true,
  getCatalogNumber,
  ...props
}: {
  links: T[];
  ClassicLine: ComputeFieldComponent<T>;
  language?: string;
  lineBreak?: boolean;
  getCatalogNumber?: (link: T) => string | undefined;
} & Omit<ComponentProps<typeof SeparatedList>, "input">): ReactNode {
  if (!links || links.length === 0) return null;

  const catalogOf =
    getCatalogNumber ??
    ((link: T) =>
      (link as unknown as RelatedExhibitionLink)
        .oeuvresDansExpositionsDescriptionNumeroCatalogue);

  const groups = new Map<string, T[]>();
  for (const link of links) {
    if (link.itinerantGroupId) {
      const list = groups.get(link.itinerantGroupId) ?? [];
      list.push(link);
      groups.set(link.itinerantGroupId, list);
    }
  }

  const entries: ReactNode[] = [];
  const emittedGroups = new Set<string>();

  for (const link of links) {
    if (link.itinerantGroupId) {
      if (emittedGroups.has(link.itinerantGroupId)) continue;
      emittedGroups.add(link.itinerantGroupId);
      const groupLinks = (groups.get(link.itinerantGroupId) ?? []).sort(
        (a, b) => (a.itinerantOrder ?? 0) - (b.itinerantOrder ?? 0),
      );
      entries.push(
        <ItinerantExhibitionsGroupCF
          key={link.itinerantGroupId}
          input={{
            stops: groupLinks.map((g) => ({
              exhibition: g.exhibition,
              catalogNumber: catalogOf(g),
              note: g.itinerantNote,
            })),
          }}
          language={language}
          {...props}
        />,
      );
    } else {
      entries.push(
        <ClassicLine
          key={link.id ?? link.exhibition?.id}
          input={link}
          language={language}
          {...props}
        />,
      );
    }
  }

  return (
    <SeparatedList
      {...props}
      language={language}
      input={entries}
      lineBreak={lineBreak}
    />
  );
}
